interface AuthResponse {
  token: string
  user?: {
    id: string
    email: string
    shop: string
  }
}

interface DecodedJWT {
  sub?: string
  email?: string
  shop?: string
  domain?: string
  shop_id?: number
  access_token?: string
  role?: string
  exp: number
  iat?: number
  jti?: string
}

class AuthService {
  private readonly TOKEN_KEY = 'shopify_jwt_token'
  private readonly USER_TOKEN_KEY = 'user_jwt_token'
  private readonly USER_KEY = 'shopify_user_info'
  private readonly DEMO_EMAIL = 'demo@netlyze.io'

  /**
   * Handle Shopify login callback with JWT
   */
  handleShopifyCallback(jwt: string): boolean {
    try {
      // Basic JWT validation (in production, verify signature)
      const decoded = this.decodeJWT(jwt)

      if (this.isTokenExpired(decoded)) {
        return false
      }

      // Store JWT token
      this.setToken(jwt)

      // Store user info - handle both old and new JWT formats
      this.setUserInfo({
        id: decoded.sub || decoded.jti || 'demo-user',
        email: decoded.email || 'demo@example.com',
        shop: decoded.shop || decoded.domain || 'demo-shop',
      })

      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Set JWT token in localStorage
   */
  setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token)
  }

  /**
   * Get JWT token from localStorage
   * Priority: user token > shop token
   */
  getToken(): string | null {
    // Check user token first
    const userToken = localStorage.getItem(this.USER_TOKEN_KEY)
    if (userToken) {
      return userToken
    }
    // Fall back to shop token
    return localStorage.getItem(this.TOKEN_KEY)
  }

  /**
   * Get user token from localStorage
   */
  getUserToken(): string | null {
    return localStorage.getItem(this.USER_TOKEN_KEY)
  }

  /**
   * Get shop token from localStorage
   */
  getShopToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY)
  }

  /**
   * Check if current token is a user token
   */
  isUserToken(): boolean {
    const userToken = localStorage.getItem(this.USER_TOKEN_KEY)
    if (!userToken) return false

    try {
      const decoded = this.decodeJWT(userToken)
      // User tokens typically have a 'role' field or don't have 'shop' field
      return !this.isTokenExpired(decoded) && (decoded.role !== undefined || decoded.shop === undefined)
    } catch {
      return false
    }
  }

  /**
   * Set user info in localStorage
   */
  setUserInfo(user: AuthResponse['user'] | any, isUserToken = false): void {
    if (user) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user))
    }
  }

  /**
   * Handle user authentication (email/password login)
   */
  handleUserAuth(data: { token: string; user: any }): void {
    // Validate token
    const decoded = this.decodeJWT(data.token)

    if (this.isTokenExpired(decoded)) {
      return
    }

    // Store user token
    localStorage.setItem(this.USER_TOKEN_KEY, data.token)

    // Store user info
    this.setUserInfo(data.user, true)
  }

  /**
   * Get user info from localStorage
   */
  getUserInfo(): AuthResponse['user'] | null {
    const userStr = localStorage.getItem(this.USER_KEY)
    if (userStr) {
      try {
        return JSON.parse(userStr)
      } catch {
        return null
      }
    }
    return null
  }

  /**
   * Check if user is authenticated
   * Checks both user token and shop token
   */
  isAuthenticated(): boolean {
    const userToken = this.getUserToken()
    const shopToken = this.getShopToken()

    // Check user token first
    if (userToken) {
      try {
        const decoded = this.decodeJWT(userToken)
        if (!this.isTokenExpired(decoded)) {
          return true
        }
      } catch {
        // Token is invalid, continue to check shop token
      }
    }

    // Check shop token
    if (shopToken) {
      try {
        const decoded = this.decodeJWT(shopToken)
        return !this.isTokenExpired(decoded)
      } catch {
        return false
      }
    }

    return false
  }

  /**
   * Get Bearer token for API requests
   */
  getBearerToken(): string | null {
    const token = this.getToken()
    return token ? `Bearer ${token}` : null
  }

  /**
   * Logout user
   * Clears both user and shop tokens
   */
  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY)
    localStorage.removeItem(this.USER_TOKEN_KEY)
    localStorage.removeItem(this.USER_KEY)
  }

  /**
   * Clear user session only (preserve shop token).
   * Used when a "demo user" session must not override shop-token access.
   */
  clearUserSession(): void {
    localStorage.removeItem(this.USER_TOKEN_KEY)
    localStorage.removeItem(this.USER_KEY)
  }

  /**
   * True if the currently stored user info represents the demo account.
   */
  isDemoAccount(): boolean {
    try {
      const u = this.getUserInfo() as any
      const email = typeof u?.email === 'string' ? u.email.toLowerCase().trim() : ''
      return email === this.DEMO_EMAIL
    } catch {
      return false
    }
  }

  /**
   * Decode JWT token (basic implementation)
   * In production, use a proper JWT library and verify signature
   */
  private decodeJWT(token: string): DecodedJWT {
    const parts = token.split('.')
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format')
    }

    const payload = parts[1]
    const decoded = JSON.parse(atob(payload))
    return decoded as DecodedJWT
  }

  /**
   * Check if JWT token is expired
   */
  private isTokenExpired(decoded: DecodedJWT): boolean {
    const currentTime = Math.floor(Date.now() / 1000)
    return decoded.exp < currentTime
  }

  /**
   * Get shop ID from token or localStorage
   * Priority:
   * 1. shop_id from current token (if shop token)
   * 2. selected_store from localStorage (for user tokens)
   * 3. shop_id from localStorage
   * 4. null if not found
   */
  getShopId(): number | null {
    try {
      const token = this.getToken()
      if (token) {
        const decoded = this.decodeJWT(token)
        // Check if token has shop_id
        if (decoded.shop_id) {
          return typeof decoded.shop_id === 'number' ? decoded.shop_id : parseInt(String(decoded.shop_id), 10)
        }
      }

      // For user tokens, check selected_store
      if (this.isUserToken()) {
        const selectedStore = localStorage.getItem('selected_store')
        if (selectedStore) {
          const shopId = parseInt(selectedStore, 10)
          if (!isNaN(shopId)) {
            return shopId
          }
        }
      }

      // Fallback to shop_id from localStorage
      const shopIdStr = localStorage.getItem('shop_id')
      if (shopIdStr) {
        const shopId = parseInt(shopIdStr, 10)
        if (!isNaN(shopId)) {
          return shopId
        }
      }

      return null
    } catch {
      return null
    }
  }

  /**
   * Redirect to Shopify OAuth
   * Replace with your actual Shopify OAuth URL
   */
  redirectToShopifyAuth(): void {
    const shopifyAuthUrl = import.meta.env.VITE_SHOPIFY_AUTH_URL || '/auth/shopify'
    window.location.href = shopifyAuthUrl
  }
}

export const authService = new AuthService()
export default authService
